import { Descriptions, Card, Typography, Space, Tag, Divider, Row, Col, Table } from 'antd';
import { FilePdfOutlined, VideoCameraOutlined, FileOutlined, LinkOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Product, ProductElement } from '../../types/product';
import { ImageGallery } from './ImageGallery';
import { AttributesTable } from './AttributesTable';
import { FeaturesSection } from './FeaturesSection';
import { QualityScoreBadge } from '../common/QualityScoreBadge';

const { Title, Text, Link } = Typography;

// Known PDF/document type names from Etilize
const PDF_TYPES = [
  'manufacturer-brochure', 'assembly-instructions', 'user-manual',
  'energy-guide', 'educational-brochure', 'product-datasheet',
  'warranty-information', 'coshh-sheet', 'product fiche',
];

function isDocumentElement(el: ProductElement): boolean {
  const t = (el.type || '').toLowerCase();
  return t === 'pdf' || t === 'document' || PDF_TYPES.includes(t)
    || t.includes('pdf') || t.includes('manual') || t.includes('brochure')
    || t.includes('datasheet') || t.includes('instructions');
}

function isVideoElement(el: ProductElement): boolean {
  const t = (el.type || '').toLowerCase();
  return t === 'video' || t.includes('video');
}

function isImageOrMedia(el: ProductElement): boolean {
  const t = (el.type || '').toLowerCase();
  return t === 'image' || t === '3d';
}

function formatTypeName(type: string): string {
  return type
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

interface PrettyViewTabProps {
  product: Product;
}

export function PrettyViewTab({ product }: PrettyViewTabProps) {
  const navigate = useNavigate();
  const documents = product.elements?.filter(isDocumentElement) || [];
  const videos = product.elements?.filter(isVideoElement) || [];
  // Anything that isn't image, 3d, document, or video
  const otherResources = product.elements?.filter(
    (el) => !isImageOrMedia(el) && !isDocumentElement(el) && !isVideoElement(el)
  ) || [];
  const allResources = [...documents, ...videos, ...otherResources];

  return (
    <div>
      <Row gutter={24}>
        <Col xs={24} md={10} lg={8}>
          <ImageGallery elements={product.elements || []} image={product.image} />
        </Col>
        <Col xs={24} md={14} lg={16}>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* Basic Info */}
            <Card size="small">
              <Title level={4} style={{ margin: 0, marginBottom: 8 }}>
                {product.description || product.mfgpartno || 'Unknown Product'}
              </Title>

              <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} size="small">
                <Descriptions.Item label="Product ID">
                  <Text copyable>{product.productid}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="MPN">
                  <Text copyable>{product.mfgpartno || '-'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="UPC/GTIN">
                  {(() => {
                    const gtin = product.skus?.find(s => s.name === 'GTIN' || s.name === 'UPC' || s.name === 'EAN');
                    return gtin ? <Text copyable>{gtin.sku}</Text> : <Text>-</Text>;
                  })()}
                </Descriptions.Item>
                <Descriptions.Item label="Manufacturer">
                  {product.manufacturer?.name ? (
                    <Link
                      onClick={() => {
                        const params = new URLSearchParams();
                        params.set('type', 'manufacturer');
                        params.set('q', product.manufacturer?.name || '');
                        navigate(`/products?${params.toString()}`);
                      }}
                    >
                      {product.manufacturer.name}
                    </Link>
                  ) : (
                    '-'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Category">
                  {product.category_name || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Quality Score">
                  <QualityScoreBadge score={product.quality_score} />
                </Descriptions.Item>
                {product.lifecycle_status && (
                  <Descriptions.Item label="Lifecycle">
                    <Tag color={
                      product.lifecycle_status === 'active' ? 'green' :
                      product.lifecycle_status === 'obsolete' ? 'orange' :
                      product.lifecycle_status === 'deleted' ? 'red' :
                      product.lifecycle_status === 'archived' ? 'default' :
                      product.lifecycle_status === 'unpublished' ? 'volcano' :
                      product.lifecycle_status === 'future' ? 'purple' : 'default'
                    }>
                      {product.lifecycle_status.toUpperCase()}
                    </Tag>
                    {product.obsolete_date && (
                      <span style={{ marginLeft: 8, color: '#999' }}>
                        EOS: {product.obsolete_date}
                      </span>
                    )}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Data Source">
                  <Space size={4} wrap>
                    <Tag color={product.data_source === 'pim' ? 'green' : 'blue'}>
                      {product.data_source}
                    </Tag>
                    {product._provider_links && product._provider_links.length > 0 ? (
                      <>
                        <Text type="secondary" style={{ fontSize: 12 }}>from</Text>
                        {product._provider_links.map((link) => (
                          <Tag
                            key={link.provider}
                            style={{ fontSize: 11, cursor: 'pointer' }}
                            onClick={() => navigate(`/products/${encodeURIComponent(link.product_id)}`)}
                          >
                            {link.provider} ({link.product_id})
                          </Tag>
                        ))}
                        {/* Show additional sources (e.g. FN-Odoo) not already covered by provider links */}
                        {product.source_providers
                          ?.filter((src) => !product._provider_links!.some((l) => l.provider === src))
                          .map((src) => (
                            <Tag
                              key={src}
                              style={{ fontSize: 11, cursor: 'pointer' }}
                              color="orange"
                              onClick={() => navigate(`/products/pim/${product.pim_product_id}/sources/${encodeURIComponent(src)}`)}
                            >
                              {src}
                            </Tag>
                          ))}
                      </>
                    ) : product.source_providers && product.source_providers.length > 0 ? (
                      <>
                        <Text type="secondary" style={{ fontSize: 12 }}>from</Text>
                        {product.source_providers.map((src) => (
                          <Tag key={src} style={{ fontSize: 11 }}>
                            {src}
                          </Tag>
                        ))}
                      </>
                    ) : null}
                  </Space>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Space>
        </Col>
      </Row>

      <Divider />

      {/* Features */}
      {product.features && product.features.length > 0 && (
        <>
          <Title level={5}>Features ({product.features.length})</Title>
          <Card size="small" style={{ marginBottom: 24 }}>
            <FeaturesSection features={product.features} />
          </Card>
        </>
      )}

      {/* Attributes */}
      {product.attributes && product.attributes.length > 0 && (
        <>
          <Title level={5}>Specifications ({product.attributes.length})</Title>
          <Card size="small" style={{ marginBottom: 24 }}>
            <AttributesTable attributes={product.attributes} />
          </Card>
        </>
      )}

      {/* Documents, Videos & Other Resources */}
      {allResources.length > 0 && (
        <>
          <Title level={5}>Resources ({allResources.length})</Title>
          <Card size="small" style={{ marginBottom: 24 }}>
            <Table
              dataSource={allResources.map((r, i) => ({ ...r, _key: i }))}
              rowKey="_key"
              size="small"
              pagination={allResources.length > 10 ? { pageSize: 10 } : false}
              columns={[
                {
                  title: '',
                  dataIndex: 'type',
                  width: 40,
                  render: (_: string, record: ProductElement) => {
                    if (isDocumentElement(record)) return <FilePdfOutlined style={{ color: '#cf1322' }} />;
                    if (isVideoElement(record)) return <VideoCameraOutlined style={{ color: '#1677ff' }} />;
                    return <FileOutlined style={{ color: '#8c8c8c' }} />;
                  },
                },
                {
                  title: 'Name',
                  dataIndex: 'propertykey',
                  render: (name: string, record: ProductElement, index: number) => {
                    let label = name;
                    if (!label) {
                      const t = (record.type || '').toLowerCase();
                      if (t === 'pdf' || t.includes('pdf')) label = `PDF Document ${index + 1}`;
                      else if (t === 'video' || t.includes('video')) label = `Video ${index + 1}`;
                      else label = formatTypeName(record.type) || `Resource ${index + 1}`;
                    }
                    return (
                      <Link href={record.url} target="_blank">
                        {label} <LinkOutlined />
                      </Link>
                    );
                  },
                },
                {
                  title: 'Type',
                  dataIndex: 'type',
                  width: 180,
                  render: (type: string) => {
                    const t = (type || '').toLowerCase();
                    let color = 'default';
                    if (t === 'pdf' || t.includes('pdf')) color = 'red';
                    else if (t === 'video' || t.includes('video')) color = 'blue';
                    return <Tag color={color}>{formatTypeName(type)}</Tag>;
                  },
                },
              ]}
            />
          </Card>
        </>
      )}
    </div>
  );
}
